/**
 * KsaDataProvider - lets the REAL Opportunities and Projects views run on the
 * GLOBAL KSA records.
 *
 * Every screen reads its data through useApp(), which returns the NEAREST
 * AppContext provider. This provider sits inside the app's own and hands the
 * views below a KSA-only value: KSA rows, and add/update/delete functions that
 * talk to the KSA API. It also sets KSA_SCOPE, which the few components that call
 * fetch() themselves use for their URL prefix, and which switches off the
 * pieces that are GlobalOps-only (Doc Hub files, finance).
 *
 * The value is an ALLOWLIST, not a copy of the parent's: anything a screen
 * could use to write GlobalOps data (addAccount, updateEngineer, addEngineer
 * ...) is simply absent here, so a mistake fails loudly instead of writing to
 * the wrong place. KSA rows never enter the parent's state or localStorage.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AppContext, useApp, toCamel, toSnake } from '../../context/AppContext';
import { DataScopeContext, KSA_SCOPE } from '../../context/DataScope';
import { inYear, ksaCustomers } from './ksaRows';
import { getRole } from '../../data/admins';
import { canSeeTab } from '../../data/roles';
import { noteSaveError } from '../../utils/saveError';
import { useLiveRefresh, latestReads } from '../../live/useLive';
import { notifyCommentsChanged } from '../../hooks/useCommentsIndex';
import { notifyDocsChanged } from '../../hooks/useDocIndex';

/**
 * Inside KSA the reused Opportunities / Projects / Tasks screens ask about
 * 'opportunities', 'projects' and 'tasks'; what they are editing is KSA
 * records, so the answer is the person's KSA level. Undefined when the
 * parent's access is not known yet (hooks/useMyAccess then falls back to the
 * role, as everywhere).
 */
export function ksaScopedAccess(access) {
  if (!access || !access.areas) return undefined;
  const k = access.areas.ksa;
  return { ...access, areas: { ...access.areas, opportunities: k, projects: k, tasks: k }, noDelete: [] };
}

/** May this roster row open KSA? Their effective areas when the roster has them, else their role. */
const opensKsa = (u, users) => canSeeTab(u.areas ? { areas: u.areas } : getRole(u.email, users), 'ksa-overview');

const JSON_HEADERS = { 'Content-Type': 'application/json' };
const NOOP = () => {};

// Same contract as the main AppContext mutators: never throw. A failed create
// is null, a failed update/delete is false - a dropped connection used to
// leave "Saving..." buttons stuck and the screens' error messages unshown.
const safe =
  (fn, fallback) =>
  async (...args) => {
    try {
      return await fn(...args);
    } catch {
      return fallback;
    }
  };
const FAILED_SAVE = Object.freeze({ ok: false, status: 0, body: null });

/**
 * The KSA records API. Deliberately NO "404 means create it" fallback like the
 * main updateProject has: a PUT that misses must never turn into an insert.
 */
export function createKsaApi(fetchFn = (...args) => fetch(...args)) {
  const url = (id) => `${KSA_SCOPE.apiBase}/projects${id ? `/${encodeURIComponent(id)}` : ''}`;
  return {
    async list() {
      const r = await fetchFn(url());
      if (!r.ok) throw new Error(`KSA records: HTTP ${r.status}`);
      const rows = await r.json();
      return Array.isArray(rows) ? toCamel(rows) : [];
    },
    add: safe(async (d) => {
      const { id, ...body } = toSnake(d || {}); // ids are the server's to make
      const r = await fetchFn(url(), { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(body) });
      return r.ok ? toCamel(await r.json()) : null;
    }, null),
    update: safe(async (id, updates) => {
      noteSaveError('');
      const r = await fetchFn(url(id), { method: 'PUT', headers: JSON_HEADERS, body: JSON.stringify(toSnake(updates || {})) });
      // keep the server's reason (e.g. a missing exchange rate) for the editor to show
      if (!r.ok)
        noteSaveError(
          await r
            .json()
            .then((b) => b && b.error)
            .catch(() => '')
        );
      return r.ok;
    }, false),
    remove: safe(async (id) => {
      const r = await fetchFn(url(id), { method: 'DELETE' });
      return r.ok;
    }, false),
    // The editors' two saves, answered whole - { ok, status, body } - the same
    // contract as the main AppContext's createProject / saveProject: a 409
    // (quote_taken, edit_conflict) is a question for the person saving.
    create: safe(async (d, { allowDuplicateQuote } = {}) => {
      const { id, ...body } = toSnake(d || {}); // ids are the server's to make
      if (allowDuplicateQuote) body.allow_duplicate_quote = true;
      const r = await fetchFn(url(), { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(body) });
      return { ok: r.ok, status: r.status, body: await r.json().catch(() => null) };
    }, FAILED_SAVE),
    save: safe(async (id, changes, { expect, force } = {}) => {
      const body = toSnake(changes || {});
      if (expect) body._expect = toSnake(expect);
      if (force) body._force = true;
      noteSaveError('');
      const r = await fetchFn(url(id), { method: 'PUT', headers: JSON_HEADERS, body: JSON.stringify(body) });
      const out = { ok: r.ok, status: r.status, body: await r.json().catch(() => null) };
      if (!r.ok) noteSaveError(out.body && out.body.error);
      return out;
    }, FAILED_SAVE),
  };
}

/** The AppContext value the reused views see. Exported for the drift test. */
export function buildKsaAppValue({ rows, loading, error, parent, load, api, quiet = load }) {
  const inYearRows = rows.filter((r) => inYear(r, parent.selectedYear));
  const after =
    (fn) =>
    async (...args) => {
      const out = await fn(...args);
      await load();
      return out;
    };
  // a refused save (409) changed nothing, so there is nothing to re-read
  const afterOk =
    (fn) =>
    async (...args) => {
      const out = await fn(...args);
      if (out && out.ok) await load();
      return out;
    };
  const deleteProject = after((id) => api.remove(id));
  return {
    loading,
    ksaError: error,
    // Owner / task pickers offer only people who can open KSA (admin,
    // operations by role, or anyone given the area): a task given to anyone
    // else would never reach them. Names still resolve for everyone.
    users: (parent.users || []).filter((u) => opensKsa(u, parent.users)),
    myAccess: ksaScopedAccess(parent.myAccess),
    resolveOwnerName: parent.resolveOwnerName,
    selectedYear: parent.selectedYear,
    setSelectedYear: parent.setSelectedYear,
    allProjects: rows,
    allOpportunities: rows,
    opportunitiesThisYear: inYearRows,
    projectsThisYear: inYearRows,
    allAccounts: ksaCustomers(rows),
    allEngineers: [],
    addProject: after((d) => api.add(d)),
    updateProject: after((id, u) => api.update(id, u)),
    createProject: afterOk((d, opts) => api.create(d, opts)),
    saveProject: afterOk((id, changes, opts) => api.save(id, changes, opts)),
    deleteProject,
    deleteOpportunity: deleteProject,
    trackRecentlyViewed: NOOP, // the sidebar's "recent" list links to main-tab records
    refresh: load,
    fetchAll: load,
    // the main context's quiet re-read after a write; the KSA list is small,
    // so its saves still await `load` (afterOk) and there is no row merge here:
    // the editor sees no applySavedProject and re-reads with fetchAll
    refreshQuietly: () => quiet(),
    applySavedProject: null,
  };
}

export default function KsaDataProvider({ children }) {
  const parent = useApp();
  const api = useMemo(() => createKsaApi(), []);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // A live re-read and a save's re-read can overlap. The newest answer GOT
  // wins (latestReads, as in AppContext): an older answer landing last never
  // puts old rows back, and a read that fails counts for nothing - so a
  // failed live re-read can never get this tab's own re-read after a save
  // thrown away (a KSA record just created stayed missing). Only a read the
  // person is waiting for - the first, the one after a save - may set the
  // error, and only while nothing newer has been shown.
  const [reads] = useState(latestReads);
  const read = useCallback(
    async (quiet) => {
      const ticket = reads.start();
      try {
        const list = await api.list();
        if (reads.show(ticket)) {
          setRows(list);
          setError('');
        }
      } catch {
        if (!quiet && reads.show(ticket)) setError('Could not load the KSA records - refresh to try again.');
      } finally {
        if (!quiet) setLoading(false);
      }
    },
    [api, reads]
  );
  const load = useCallback(() => read(false), [read]);
  useEffect(() => {
    load();
  }, [load]);

  // Someone else's change to the KSA records (src/live; kind 'ksa' reaches
  // only the people allowed to read them): a new record, an edit, its costs,
  // an exchange rate. Re-read quietly - a failure keeps the rows on screen
  // and shows no error; the next change or a resync catches up.
  const reloadQuietly = useCallback(() => read(true), [read]);
  useLiveRefresh(KSA_SCOPE.key, reloadQuietly, { match: (e) => e.sub !== 'crm-actions' && e.sub !== 'attachments' });
  // The tables' comment column and Doc Hub links over these rows. A KSA event
  // does not say whether a record's note, file or own fields changed, so a
  // change to a record refreshes both. (Tasks listen in tasks/useTasks.js.)
  useLiveRefresh(KSA_SCOPE.key, notifyCommentsChanged, { match: (e) => e.sub === 'projects' });
  useLiveRefresh(KSA_SCOPE.key, notifyDocsChanged, { match: (e) => e.sub === 'projects' || e.sub === 'attachments' });

  // myAccess too: without it every screen on the KSA tab fell back to the
  // role defaults, ignoring the person's own overrides (and the Quotes Edit
  // that shows the KSA quote rates - 10/7/26)
  const { users, myAccess, resolveOwnerName, selectedYear, setSelectedYear } = parent;
  const value = useMemo(
    () =>
      buildKsaAppValue({
        rows,
        loading,
        error,
        parent: { users, myAccess, resolveOwnerName, selectedYear, setSelectedYear },
        load,
        api,
        quiet: reloadQuietly,
      }),
    [rows, loading, error, users, myAccess, resolveOwnerName, selectedYear, setSelectedYear, load, api, reloadQuietly]
  );

  return (
    <DataScopeContext.Provider value={KSA_SCOPE}>
      <AppContext.Provider value={value}>{children}</AppContext.Provider>
    </DataScopeContext.Provider>
  );
}
